import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowLeft } from "lucide-react";
import { getServices, getCompanyInfo } from "../services/api";
import SEO from "../components/SEO";
import Header from "../components/Header";
import Footer from "../components/Footer";

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, companyData] = await Promise.all([
          getServices(),
          getCompanyInfo()
        ]);
        setServices(servicesData);
        setCompanyInfo(companyData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div>
        <SEO
          title="Septic Services Thunder Bay | Installation, Repair & Replacement"
          description="Septic installers in Thunder Bay and Northwestern Ontario. System installation, drain field and tank replacement, and repairs. No pumping – design, installation & replacement only."
          canonicalPath="/services"
        />
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="Septic Services Thunder Bay | Installation, Repair & Replacement"
        description="Septic installers in Thunder Bay and Northwestern Ontario. System installation, drain field and tank replacement, and repairs. No pumping – design, installation & replacement only."
        canonicalPath="/services"
      />
      <Header />

      <div className="pt-32 pb-16" style={{ backgroundColor: "var(--white)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Navigation */}
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center text-sm font-medium hover:opacity-70"
              style={{ color: "var(--primary-orange)" }}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Link>
          </div>

          {/* Page Header */}
          <div className="gh-page-heading text-center mb-16" data-reveal>
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: "var(--dark-navy)" }}
            >
              Our Septic Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional septic system installation, design, and repair services throughout Northwestern Ontario.
            </p>
          </div>

          {/* Overview Paragraph */}
          <section className="mb-10 max-w-4xl mx-auto text-left">
            <p
              className="text-lg leading-relaxed"
              style={{ color: "var(--text-light)" }}
            >
              Groundhogs provides complete septic services in Thunder Bay and across Northwestern Ontario, engineered specifically
              for our region's challenging soil and climate. From clay-dominant properties and shallow bedrock to high water tables
              and severe freeze–thaw cycles, every system we install or repair is designed to perform in real Northern Ontario
              conditions. Whether you are searching for <strong>"septic installers near me"</strong>, need urgent
              <strong> septic repair in Thunder Bay</strong>, or are planning a full <strong>septic system installation in Thunder Bay</strong>,
              our team manages the entire process to strict Ontario Building Code (OBC) standards with engineering support where
              required. With more than <strong>1,200+ septic installations</strong> completed, we handle installations, repairs, tank
              replacements, and drain field replacements for residential and rural properties throughout the region.
            </p>
          </section>

          {/* Value Statements */}
          <section className="mb-16">
            <div className="grid gap-6 md:grid-cols-3 text-center">
              <div className="p-4 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
                <h2
                  className="text-lg font-bold mb-1"
                  style={{ color: "var(--dark-navy)" }}
                >
                  1,200+ Septic Installations Across Northwestern Ontario
                </h2>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Proven experience with Thunder Bay's clay soil, bedrock, and high-water-table properties.
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
                <h2
                  className="text-lg font-bold mb-1"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Certified OBC &amp; Eljen System Specialists
                </h2>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Fully compliant with Ontario Building Code and factory-trained for Eljen GSF advanced treatment systems.
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
                <h2
                  className="text-lg font-bold mb-1"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Fast Turnaround &mdash; Most Projects Completed in About 3 Days on Average
                </h2>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Efficient crews and equipment minimize disruption while delivering long-lasting septic solutions.
                </p>
              </div>
            </div>
          </section>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="p-8">
                  <h2
                    className="text-2xl font-bold mb-4"
                    style={{ color: "var(--dark-navy)" }}
                  >
                    {service.title}
                  </h2>
                  <p className="text-gray-600 mb-6">{service.description}</p>

                  {/* Features List */}
                  {service.features && (
                    <ul className="space-y-3 mb-6">
                      {service.features.slice(0, 4).map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle
                            className="mr-2 mt-1 flex-shrink-0"
                            size={20}
                            style={{ color: "var(--primary-orange)" }}
                          />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Local service coverage line and keyword-rich link for key services */}
                  {service.slug === "septic-system-installation-thunder-bay" && (
                    <>
                      <p
                        className="text-sm mb-2"
                        style={{ color: "var(--text-light)" }}
                      >
                        Serving Thunder Bay, Shuniah, Murillo, Oliver Paipoonge, Kaministiquia, and nearby lakefront communities.
                      </p>
                      <p className="text-sm font-medium mb-4">
                        <Link
                          to="/services/septic-system-installation-thunder-bay"
                          className="text-orange-600 hover:underline"
                        >
                          Learn more about our Septic System Installation services in Thunder Bay &rarr;
                        </Link>
                      </p>
                    </>
                  )}

                  {service.slug === "septic-field-installation-replacement-thunder-bay" && (
                    <>
                      <p
                        className="text-sm mb-2"
                        style={{ color: "var(--text-light)" }}
                      >
                        Serving Thunder Bay, Nolalu, Pass Lake, Gorham, Neebing, and surrounding rural properties.
                      </p>
                      <p className="text-sm font-medium mb-4">
                        <Link
                          to="/services/septic-field-installation-replacement-thunder-bay"
                          className="text-orange-600 hover:underline"
                        >
                          Learn more about our Septic Field Replacement services in Northwestern Ontario &rarr;
                        </Link>
                      </p>
                    </>
                  )}

                  {service.slug === "septic-tank-installation-thunder-bay" && (
                    <>
                      <p
                        className="text-sm mb-2"
                        style={{ color: "var(--text-light)" }}
                      >
                        Serving Thunder Bay, Shuniah, Red Rock, Nipigon, Murillo, and nearby communities along Lake Superior.
                      </p>
                      <p className="text-sm font-medium mb-4">
                        <Link
                          to="/services/septic-tank-installation-thunder-bay"
                          className="text-orange-600 hover:underline"
                        >
                          Learn more about our Septic Tank Installation services in Thunder Bay &rarr;
                        </Link>
                      </p>
                    </>
                  )}

                  {service.slug === "septic-repair-near-me" && (
                    <>
                      <p
                        className="text-sm mb-2"
                        style={{ color: "var(--text-light)" }}
                      >
                        Serving Thunder Bay, Gorham, Conmee, O'Connor, Oliver Paipoonge, and nearby townships.
                      </p>
                      <p className="text-sm font-medium mb-4">
                        <Link
                          to="/services/septic-repair-near-me"
                          className="text-orange-600 hover:underline"
                        >
                          Learn more about our Septic Repair services in Thunder Bay &rarr;
                        </Link>
                      </p>
                    </>
                  )}

                  {/* Existing Learn More button */}
                  <Link
                    to={`/services/${service.slug}`}
                    className="inline-block px-6 py-3 rounded-md font-medium text-white hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* FAQ Section for Septic Services in Thunder Bay */}
          <section className="mt-8 mb-12">
            <h2
              className="text-3xl font-bold mb-6 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Frequently Asked Questions About Septic Services in Thunder Bay
            </h2>
            <div
              className="max-w-3xl mx-auto space-y-6"
              style={{ color: "var(--text-light)" }}
            >
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How long do septic field replacements take in Thunder Bay?
                </h3>
                <p>
                  A: Most replacements take 2&ndash;4 days depending on soil conditions, groundwater levels, and access.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do you offer emergency septic repair?
                </h3>
                <p>
                  A: Yes &mdash; 24/7 emergency service for sewage backups, pump failures, and overflowing tanks.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do you install Eljen systems in Thunder Bay?
                </h3>
                <p>
                  A: Yes &mdash; we install Eljen GSF advanced treatment systems for clay soil, bedrock, small lots,
                  and high-water-table properties.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How often should I schedule a septic inspection in Northern Ontario?
                </h3>
                <p>
                  A: Every 1&ndash;3 years, with additional inspections before winter or when buying/selling a home.
                </p>
              </div>
            </div>
          </section>

          {/* Localized CTA Block */}
          {companyInfo && (
            <section
              className="mt-4 mb-16 text-center py-10 px-6 rounded-lg"
              style={{ backgroundColor: "var(--dark-navy)" }}
            >
              <p className="text-xl mb-6 text-white">
                Serving all of Thunder Bay and Northwestern Ontario &mdash; get your septic system repaired
                or installed by the region's most trusted experts.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/contact"
                  className="inline-block px-8 py-4 rounded-md font-semibold text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--primary-orange)" }}
                >
                  Get Free Estimate
                </Link>
                <a
                  href={`tel:${companyInfo.phone || "1-888-907-3777"}`}
                  className="inline-block px-8 py-4 rounded-md font-semibold border-2 text-white hover:bg-white hover:text-orange-600"
                  style={{ borderColor: "var(--primary-orange)" }}
                >
                  Call {companyInfo.phone || "1-888-907-3777"}
                </a>
              </div>
            </section>
          )}

          {/* Original CTA Section */}
          {companyInfo && (
            <div
              className="rounded-lg p-8 text-center"
              style={{ backgroundColor: "var(--dark-navy)" }}
            >
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: "var(--white)" }}
              >
                Ready to Get Started?
              </h2>
              <p className="text-xl mb-6" style={{ color: "var(--white)" }}>
                Contact us today for a free estimate on your septic system project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-8 py-4 rounded-md font-medium text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: "var(--primary-orange)" }}
                >
                  Get Free Estimate
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="px-8 py-4 rounded-md font-medium border-2 hover:opacity-90 transition-opacity"
                  style={{ color: "var(--white)", borderColor: "var(--white)" }}
                >
                  Call {companyInfo.phone}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ServicesPage;
